import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Sparkles, Clock, MessageCircle, CreditCard, Wand2 } from "lucide-react";
import { IMAGES, WHATSAPP_LINK, LOGO_URL } from "../../constants";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LogoWatermark = () => (
  <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.04, transform: 'rotate(-15deg)', transformOrigin: 'center center' }}>
    <div className="flex flex-col gap-8" style={{ marginTop: '-80px', marginLeft: '-80px', width: 'calc(100% + 160px)', height: 'calc(100% + 160px)' }}>
      {[...Array(10)].map((_, row) => (
        <div key={`wm-row-${row}`} className="flex gap-12 items-center" style={{ marginLeft: row % 2 === 0 ? '0px' : '80px' }}>
          {[...Array(12)].map((_, col) => (
            <img key={`wm-col-${col}`} src={LOGO_URL} alt="" className="h-16 w-auto flex-shrink-0 select-none" draggable={false} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Party celebration" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFFAF5]/95 via-[#FFFAF5]/85 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="hero-content">
            <div className="inline-flex items-center gap-2 bg-[#E83E8C]/10 text-[#E83E8C] px-4 py-2 rounded-full mb-6 animate-fade-in-up">
              <Sparkles size={16} />
              <span className="text-sm font-semibold">House Party Specialists</span>
            </div>

            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E1B4B] mb-6 animate-fade-in-up stagger-1">
              Ghar ki party ab banegi <span className="gradient-text">WOW</span>
            </h1>

            <p className="text-lg md:text-xl text-[#475569] mb-8 max-w-xl animate-fade-in-up stagger-2">
              Complete house party setups starting <span className="text-[#E83E8C] font-bold">₹3,999</span> — Decor, Cake, Catering & More
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-8 animate-fade-in-up stagger-3">
              <a href="#book-online" className="flex items-center justify-center gap-2 bg-[#E83E8C] hover:bg-[#d62f7a] text-white px-6 sm:px-8 py-4 rounded-full font-bold text-base sm:text-lg transition-all hover:scale-105 shadow-lg btn-shine" data-testid="hero-book-online-btn">
                <CreditCard size={22} /> Book Online Now
              </a>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white px-6 sm:px-8 py-4 rounded-full font-bold text-base sm:text-lg transition-all hover:scale-105 shadow-lg btn-shine" data-testid="hero-whatsapp-btn">
                <MessageCircle size={22} /> Book on WhatsApp
              </a>
              <Link to="/doozygo-magic" className="flex items-center justify-center gap-2 bg-[#1E1B4B] hover:bg-[#2d1b69] text-white px-6 sm:px-8 py-4 rounded-full font-bold text-base sm:text-lg transition-all hover:scale-105 shadow-lg btn-shine" data-testid="hero-magic-btn">
                <Wand2 size={22} className="text-[#FFD166]" /> Try DoozyGo Magic
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-[#475569] animate-fade-in-up stagger-4">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (<Star key={`hero-star-${i}`} size={18} className="fill-[#FFD166] text-[#FFD166]" />))}
              </div>
              <span className="font-medium">500+ Happy Families</span>
              <span className="text-[#E83E8C]">|</span>
              <span className="flex items-center gap-1"><Clock size={16} /> Setup in Just 2 Hours</span>
            </div>
          </div>

          <div className="hidden lg:block relative">
            <div className="absolute top-10 right-10 w-20 h-20 bg-[#FFD166] rounded-full opacity-60 animate-float"></div>
            <div className="absolute bottom-20 left-10 w-16 h-16 bg-[#E83E8C] rounded-full opacity-40 animate-float" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-1/2 right-20 w-12 h-12 bg-[#FF9F1C] rounded-full opacity-50 animate-float" style={{animationDelay: '0.5s'}}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const TransformationSection = () => {
  const [pairs, setPairs] = useState(IMAGES.beforeAfter.map((p, i) => ({ before: p.before, after: p.after, isReal: false, key: `static-${i}` })));

  // Pull REAL paired before/after images from recent magic_jobs. The old static
  // set used an Unsplash "before" + a /gallery "after" from a different room,
  // which looked dishonest. If any real pairs are available we overwrite.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/magic/showcase-pairs?limit=3`);
        if (!res.ok) return;
        const data = await res.json();
        const list = (data.pairs || []).filter(p =>
          (p.before_b64 && p.after_b64) || (p.before_url && p.after_url)
        );
        if (!cancelled && list.length) {
          setPairs(list.map((p, i) => {
            const hasB64 = p.before_b64 && p.after_b64;
            return {
              before: hasB64 ? `data:image/jpeg;base64,${p.before_b64}` : p.before_url,
              after: hasB64 ? `data:image/jpeg;base64,${p.after_b64}` : p.after_url,
              isReal: hasB64,
              isCurated: !!p.is_curated,
              key: p.job_id || `real-${i}`,
            };
          }));
        }
      } catch { /* keep static fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="py-20 md:py-28 bg-white relative overflow-hidden" data-testid="transformation-section">
      <LogoWatermark />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#1E1B4B] mb-4">See How We Transform Your Home into a Party Zone</h2>
          <div className="section-divider mb-4"></div>
          <p className="text-[#475569] text-lg">From simple space to celebration magic — That's the DoozyGo® experience</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {pairs.map((item, idx) => (
            <div key={item.key} className="group relative rounded-3xl overflow-hidden card-shadow cursor-pointer" data-testid={`transformation-card-${idx}`}>
              <div className="relative aspect-[4/3]">
                <img src={item.before} alt="Before decoration" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-0" />
                <img src={item.after} alt="After decoration" className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute top-4 left-4 bg-[#1E1B4B] text-white px-3 py-1 rounded-full text-sm font-medium transition-opacity duration-300 group-hover:opacity-0">Before</div>
                <div className="absolute top-4 left-4 bg-[#E83E8C] text-white px-3 py-1 rounded-full text-sm font-medium opacity-0 transition-opacity duration-300 group-hover:opacity-100">After</div>
                {item.isReal && (
                  <div className="absolute top-4 right-4 bg-[#25D366]/90 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">Real customer</div>
                )}
                {item.isCurated && !item.isReal && (
                  <div className="absolute top-4 right-4 bg-[#1E1B4B]/90 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">DoozyGo® setup</div>
                )}
                <div className="absolute bottom-4 left-4 right-4 text-center bg-white/90 backdrop-blur-sm py-2 rounded-full text-sm font-medium text-[#475569] opacity-0 group-hover:opacity-100 transition-opacity">Hover to see the magic!</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
